import React, { useState } from "react";
import "../styles/LoadingOverlay.css";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

const LoadingOverlay = () => (
  <div className="loading-overlay-modern fixed inset-0 flex items-center justify-center z-50">
    <div className="spinner">
      <div className="double-bounce1"></div>
      <div className="double-bounce2"></div>
    </div>
  </div>
);

const StripePaymentForm = ({ formData, onPaymentSuccess, setStep }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [cardholderName, setCardholderName] = useState("");
  const [cardReady, setCardReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const ErrorPopup = ({ message, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full text-center">
        <h2 className="text-base font-bold text-red-600 mb-2">Errore di autorizzazione</h2>
        <p className="text-xs text-gray-700 mb-4">La tua carta è stata rifiutata. Riprova o utilizza un altro metodo di pagamento.</p>
        <button
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
          onClick={onClose}
        >
          Chiudi
        </button>
      </div>
    </div>
  );

  const handleCheckout = async () => {
    if (processing) return;
    setProcessing(true);
    setLoading(true);

    console.log("🔁 Starting payment...");

    const cardElement = elements?.getElement(CardNumberElement);

    if (!stripe || !elements || !cardElement) {
      console.error("❌ Stripe not ready");
      setProcessing(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/create-payment-intent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          clientType: formData.clientType,
          cardholderName,
        }),
      });

      console.log("✅ Backend responded");

      const text = await response.text();
      console.log("📦 Raw response from backend:", text);

      let clientSecret;
      try {
        clientSecret = JSON.parse(text).clientSecret;
      } catch (e) {
        console.error("❌ Failed to parse JSON:", e.message);
        return;
      }

      console.log("🎯 Client Secret:", clientSecret);

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: cardholderName,
          },
        },
      });

      console.log("🎉 Payment Result:", paymentIntent);

      if (error) {
        console.error("❌ Payment error", error);
        setErrorMessage(error.message || "Si è verificato un errore. Riprova.");
        return;
      } else if (paymentIntent?.status === "succeeded") {
        if (onPaymentSuccess) onPaymentSuccess();
        setStep && setStep(4); // Move to thank-you page
      } else if (paymentIntent?.status === "requires_capture") {
        // Build the full form data to send
        const fullFormData = {
          name: formData.name || "",
          email: formData.email || "",
          phone: formData.phone || "",
          clientType: formData.clientType || "",
          selectedDate: formData.selectedDate || "",
          selectedTime: formData.selectedTime || "",
          codiceFiscale: formData.codiceFiscale || "",
          modalita: formData.modalita || "",
          motivo: formData.motivo || ""
        };

        // Send confirmation emails
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/send-confirmation-emails`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ formData: fullFormData })
        });


        // Show success alert to the user
        if (onPaymentSuccess) onPaymentSuccess();
        setStep && setStep(4); // Move to thank-you page
      } else {
        console.warn("⚠️ Payment status:", paymentIntent?.status);
        setStep && setStep(6); // Move to error/failure page
      }
    } catch (error) {
      setErrorMessage("Si è verificato un errore. Riprova.");
      console.error("❌ Caught error:", error);
    } finally {
      setProcessing(false);
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingOverlay />}
      <div className="transition-opacity duration-500 ease-in-out opacity-100 animate-fade-in">
      <div className="w-full max-w-sm bg-gray-100 p-4 rounded mx-auto">
        <div className="flex flex-col items-center mb-4 w-full">
          <img
            src="/studiogenise5.png"
            alt="Studio Genise Logo"
            className="h-24 mb-6"
          />
          <h2 className="text-lg font-semibold text-[#304563] w-full text-center">Studio Genise</h2>
          <p className="text-sm text-gray-700 mb-4 w-full text-center">
            {formData.clientType === "privato / forfettario"
              ? "Consulenza Privato / Forfettario - €127,45"
              : "Consulenza Aziendale / P.IVA - €175,54"}
          </p>
          <p className="text-xs text-gray-500 mb-4 w-full text-center">
            L’importo sarà addebitato dopo la consulenza.
          </p>
        </div>

        <div className="flex flex-col items-center justify-center w-full">
          <label className="block mb-2 text-sm text-gray-700 w-full">Nome sulla carta</label>
          <input
            type="text"
            value={cardholderName}
            onChange={(e) => setCardholderName(e.target.value)}
            placeholder="Mario Rossi"
            className="w-full px-3 py-2 border rounded mb-4 text-sm"
          />

          <label className="block mb-2 text-sm text-gray-700 w-full">Numero della carta</label>
          <div className="p-2 border rounded mb-4 bg-white w-full">
            <CardNumberElement className="w-full" onReady={() => setCardReady(true)} />
          </div>

          <label className="block mb-2 text-sm text-gray-700 w-full">Data di scadenza</label>
          <div className="p-2 border rounded mb-4 bg-white w-full">
            <CardExpiryElement className="w-full" />
          </div>

          <label className="block mb-2 text-sm text-gray-700 w-full">CVC</label>
          <div className="p-2 border rounded mb-4 bg-white w-full">
            <CardCvcElement className="w-full" />
          </div>

          <button
            className="mt-4 w-full px-4 py-2 bg-[#304563] text-white rounded hover:bg-[#223249] disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleCheckout}
            disabled={!stripe || !elements || !cardReady || processing}
          >
            {loading ? (
              <span className="flex items-center justify-center space-x-2">
                <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
                <span>Attendere...</span>
              </span>
            ) : (
              "Autorizza Pagamento"
            )}
          </button>
        </div>
      </div>
        {errorMessage && (
          <ErrorPopup message={errorMessage} onClose={() => setErrorMessage("")} />
        )}
      </div>
    </>
  );
};

export default StripePaymentForm;